import React, {
  useReducer,
  useEffect,
  useState,
  useCallback,
  Fragment,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import isEqual from 'lodash/isEqual';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

// eslint-disable-next-line import/no-extraneous-dependencies
import { Icon } from 'legacy-packages/ft-cb';
import LearnosityProvider from 'legacy/src/utility/LearnosityProvider';
import {
  createResponseAnalysis,
  customCbRubricsConfSetter,
  deserialiseScoringRubricResponses,
  hasAllRubricsFilled,
} from 'legacy/src/components/learnosity/utils/helpers';
import ScoringItem from 'legacy/src/components/scoring/ScoringItem';
import ScoringNav from 'legacy/src/components/scoring/ScoringNav';
import FeedbackButton from 'legacy/src/components/assignment/components/IndividualFeedback/FeedbackButton';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import { isMCQ } from 'legacy-src/utility/progressHelpers';
import { MobileMedia } from 'legacy-src/utility/ResponsiveView';
import './ScoringLayout.scss';
import DropdownFilter from '../assessment/Filters/components/DropdownFilter';

import { toPairs } from 'lodash';
import useCustomRubrics from 'legacy/src/components/learnosity/hooks/useCustomRubrics';
import Button from 'chameleon/ui-stack/ui/base/button';

const { entries, keys, values } = Object;

function learnosityReducer(state, action) {
  const items = action.payload.getItems && action.payload.getItems();

  switch (action.type) {
    case 'onStudentReviewReady':
      return {
        ...state,
        studentResponseForReference: keys(items).reduce(
          (ids, reference) => ({
            ...ids,
            [reference]: items[reference].response_ids[0],
          }),
          {},
        ),
        questionsApp: action.payload,
        questionsWithApi: action.payload.questions(),
        items,
        scores: action.payload.getScores(),
        isStudentSessionReady: true,
      };

    case 'onLearnosityDataResponseAnalysisReady':
      return {
        ...state,
        reportAnalysisData: action.payload,
      };

    case 'onLearnosityDataScoringRubricResponseAnalysisReady':
      return {
        ...state,
        scoringReportAnalysisData: action.payload,
      };

    case 'onScoringRubricReady':
      return {
        ...state,
        scoringRubricItemsApp: action.payload,
        scoringRubricItems: items,
        scoringRubricQuestionsApi: action.payload.questions(),
        isScoringRubricReady: true,
      };
    default:
      throw Error('action type is not supported');
  }
}

function scoresReducer(state, action) {
  switch (action.type) {
    case 'mcqQuestionScores':
      const mcqScores = { ...state.mcqScores, ...action.payload.scores };
      return {
        ...state,
        mcqScores,
        savedMCQScore: values(mcqScores)
          .map((score) => score.score)
          .reduce((curr, next) => curr + next, 0),
      };
    case 'frqQuestionScores':
      const frqScores = { ...state.frqScores, ...action.payload.scores };
      return {
        ...state,
        frqScores,
        maxFRQScore: values(frqScores)
          .map((score) => score.maxScore)
          .reduce((curr, next) => curr + next, 0),
        currentFRQScore: values(frqScores)
          .map((score) => score.score)
          .reduce((curr, next) => curr + next, 0),
      };
    case 'answeredQuestions':
      return {
        ...state,
        answeredQuestions: [
          ...state.answeredQuestions,
          ...action.payload.questions,
        ],
      };
    default:
      throw Error('action type is not supported');
  }
}

const extractResponseValues = (learnosityResponses) =>
  /*
    Learnosity responses have the following format:

    {
      responseId: {
        apiVersion: ...
        revision: ...
        type: ...
        value: actualValue
      },
      ...
    }

    Here we extract actual values from them:

    {
      responseId: actualValue,
      ...
    }

  */
  learnosityResponses &&
  toPairs(
    entries(learnosityResponses).map(([responseId, responseData]) => [
      responseId,
      responseData.value,
    ]),
  );

export const TABS = {
  details: 'details',
  responses: 'responses',
};

function ScoringLayout({
  studentSessionReviewSignedRequest,
  scoringRubricSignedRequest,
  responseAnalysisSignedRequest,
  scoringRubricResponseAnalysisSignedRequest,
  questions,
  params,
  returnPath,
  pushPathname,
  location,
  onSubmit,
  onSave,
  onClose,
  scoringStudent,
  assignment,
  isFinalExam,
  charmUpdateFlag,
  preApCharmUpdateFlag,
  isApSubject,
  showHeader,
  canSave,
  showActionButtons,
  isStudent,
  subjectData,
  featureFlags,
  submissions,
  showStudentScoringSwitch,
  refetchProgress,
  progressPerStudent,
  showCumulativeScore,
  disableAutoSaveOnReview,
  saveToBrowser,
  responsesPage,
  isImpersonating,
  isApReader,
  scoringRubricResponses,
}) {
  const [isAllScored, setIsAllScored] = useState(false);
  const [sectionToGo, setSectionToGo] = useState(0);
  const [isConfirmSubmitOpened, setIsConfirmSubmitOpened] = useState(false);
  const [navigateToURL, setNavigateToURL] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedFRQScore, setSavedFRQScore] = useState(null);
  const [savedScoringRubricResponses, setSavedScoringRubricResponses] =
    useState(null);
  const [currentScoringRubricResponses, setCurrentScoringRubricResponses] =
    useState(null);
  const [assignmentFeedback, setFeedback] = useState('');
  const [openFeedbackPanel, setOpenFeedbackPanel] = useState(false);
  const [tabData, setTabData] = useState({
    selectedTabIndex: 0,
    mobileTab: TABS.details,
  });

  const { title, id, printToggle: assignedAsPrintTest } = assignment;

  useCustomRubrics({
    scoringRubricResponseAnalysis: scoringRubricResponseAnalysisSignedRequest,
    assignment,
    progressPerStudent,
  });

  if (scoringRubricResponses) {
    customCbRubricsConfSetter({
      data: deserialiseScoringRubricResponses(
        scoringRubricResponses,
        params.studentId,
      ),
      assignment,
      progressPerStudent,
    });
  }

  useEffect(() => {
    const feedback = get(assignment, 'feedback[0].content');
    setOpenFeedbackPanel(
      feedback &&
        assignment &&
        assignment.progress &&
        (assignment.progress.studentScored ||
          assignment.progress.teacherAndStudentScored),
    );
    setFeedback(feedback);
  }, [assignment]);

  const [learnosityState, dispatch] = useReducer(learnosityReducer, {
    studentResponseForReference: [],
    questionsApp: null,
    scoringRubricItemsApp: null,
    isStudentSessionReady: false,
    isScoringRubricReady: false,
    scoringReportAnalysisData: scoringRubricResponses,
    questionsWithApi: {},
    items: {},
    scoringRubricItems: {},
    scores: [],
    scoringRubricQuestionsApi: {},
    reportAnalysisData: null,
  });

  const [scoresState, dispatchScore] = useReducer(scoresReducer, {
    mcqScores: {},
    frqScores: {},
    answeredQuestions: [],
    savedMCQScore: null,
    maxFRQScore: null,
    currentFRQScore: null,
  });

  const {
    studentResponseForReference,
    questionsWithApi,
    scoringRubricItemsApp,
    questionsApp,
    isScoringRubricReady,
    items,
    isStudentSessionReady,
    reportAnalysisData,
  } = learnosityState;

  const { answeredQuestions, savedMCQScore, maxFRQScore, currentFRQScore } =
    scoresState;

  const visibleQuestionId = params.questionId;
  const visibleQuestion = questions.find((q) => q.id === visibleQuestionId);
  const visibleQuestionIndex = questions.indexOf(visibleQuestion);
  const visiblePartId = params.partId || 0;
  const visiblePart =
    visibleQuestion &&
    visibleQuestion.children &&
    visibleQuestion.children.length &&
    visibleQuestion.children.find(({ id }) => id === visiblePartId);
  const visiblePartIndex =
    visiblePart && visibleQuestion.children.indexOf(visiblePart);

  useEffect(() => {
    if (visiblePartIndex && visiblePartIndex >= 0) {
      setSectionToGo(visiblePartIndex);
    }
  }, [visiblePartIndex, visibleQuestionIndex]);

  const isSubmissionScored =
    submissions &&
    (submissions[scoringStudent.id] === 'scored' ||
      submissions[scoringStudent.id] === 'teacher_and_student_scored');

  const triggerAutoSave = useCallback(() => {
    // Auto-saving is disabled on submissions that have been scored already.
    // Teacher has to click "Submit" to re-score them.
    if (disableAutoSaveOnReview && isSubmissionScored) {
      return;
    }
    if (canSave) {
      onSave(scoringRubricItemsApp, true);
    }
  }, [isSubmissionScored, scoringRubricItemsApp]);

  const navigateToQuestion = (questionId) => {
    const questionPath = location.pathname.replace(
      /\/questions\/(.*)/,
      `/questions/${questionId}`,
    );
    pushPathname(questionPath);
  };

  const goToQuestion = (isForward, goToLast) => {
    const from = isForward ? 1 : -1;
    const questionToGo = questions[visibleQuestionIndex + from];

    if (questionToGo) {
      navigateToQuestion(questionToGo.id);

      const sectionNumber = goToLast
        ? questionToGo.children && questionToGo.children.length - 1
        : 0;
      setSectionToGo(sectionNumber);

      // Save questions every time user clicks prev/next
      triggerAutoSave();
    }
  };

  const goToQuestionIndex = (index) => {
    const questionToGo = questions[index];
    if (questionToGo) {
      // typeset it before move
      navigateToQuestion(questionToGo.id);
      setSectionToGo(0);
    }
  };

  // (re)add on-change handlers to scoring rubric questions
  // The handlers may behave differently depending on the component state, that's why we need to re-add them.
  useEffect(() => {
    const questionsAndHandlers = [];
    if (isScoringRubricReady) {
      entries(scoringRubricItemsApp.getItems()).forEach(
        ([itemReference, itemData]) => {
          itemData.questions.forEach((questionData) => {
            const question = scoringRubricItemsApp.question(
              questionData.response_id,
            );
            const onChange = () => {
              setIsAllScored(hasAllRubricsFilled(scoringRubricItemsApp));
              triggerAutoSave();
              setCurrentScoringRubricResponses(
                scoringRubricItemsApp.getResponses(),
              );
              dispatchScore({
                type: 'frqQuestionScores',
                payload: {
                  scores: {
                    [itemReference]: {
                      ...scoresState.frqScores[itemReference],
                      score: Number(question.getResponse().value.score),
                      scored: true,
                    },
                  },
                },
              });
            };
            question.on('changed', onChange);
            questionsAndHandlers.push([question, onChange]);
          });
        },
      );
    }
    return () => {
      questionsAndHandlers.forEach(([question, handler]) => {
        question.off('changed', handler);
      });
    };
  }, [isScoringRubricReady, triggerAutoSave, scoresState]);

  useEffect(() => {
    if (isScoringRubricReady) {
      setIsAllScored(hasAllRubricsFilled(scoringRubricItemsApp));
      const responses = scoringRubricItemsApp.getResponses();
      setCurrentScoringRubricResponses(responses);
      setSavedScoringRubricResponses(responses);

      // Go to last attempted item on initialization
      const attemptedItems = scoringRubricItemsApp.attemptedItems();
      const allItems = learnosityState.items;
      const allResponses = scoringRubricItemsApp.getResponses();
      const firstQuestionParts = questions[0].children;
      let lastAttempt = firstQuestionParts
        ? firstQuestionParts[0].learnosityItemReference
        : questions[0].learnosityItemReference;
      const shouldRestore = visibleQuestionIndex === 0;

      if (canSave && shouldRestore && attemptedItems.length > 1) {
        // Look for non-scored questions
        let currentQuestion = 0;
        let nonScoredFound = false;
        do {
          let currentCategory = 0;

          const parts = questions[currentQuestion].children;
          if (parts) {
            do {
              const currentItemReference =
                parts && parts[currentCategory].learnosityItemReference;
              const currentResponseId =
                currentItemReference &&
                allItems[currentItemReference] &&
                allItems[currentItemReference].response_ids[0];

              const currentResponseScore =
                currentResponseId &&
                allResponses[currentResponseId] &&
                allResponses[currentResponseId].value &&
                allResponses[currentResponseId].value.score;

              if (!currentResponseScore) {
                lastAttempt =
                  parts && parts[currentCategory].learnosityItemReference;
                nonScoredFound = true;
              }

              currentCategory++;
            } while (currentCategory < parts.length && !nonScoredFound);
          }

          currentQuestion++;
        } while (currentQuestion < questions.length && !nonScoredFound);

        // Get last attempted question and category index.
        const questionIndex = questions.findIndex(
          (q) => q.learnosityItemReference === lastAttempt.split('_')[0],
        );

        if (questionIndex !== -1) {
          const { children } = questions[questionIndex];
          if (children) {
            const catIndex = children.findIndex(
              (c) => c.learnosityItemReference === lastAttempt,
            );

            if (catIndex !== -1) {
              // Go to last attempted question and category
              navigateToQuestion(questions[questionIndex].id);
              setSectionToGo(catIndex);
            }
          }
        }
      }
    }
  }, [isScoringRubricReady]);

  useEffect(() => {
    if (isScoringRubricReady && isStudentSessionReady) {
      const scoringRubricQuestions = scoringRubricItemsApp.questions();
      const frqScores = {};

      values(scoringRubricQuestions).forEach((question) => {
        const questionObject = question.getQuestion();
        const questionResponse = question.getResponse();

        const { max_score: rubricsMaxScore, score: rubricsScore } =
          questionResponse ? question.getScore() : { score: 0, max_score: 0 };

        const scoringRubricItem = values(
          learnosityState.scoringRubricItems,
        ).find((item) => item.response_ids[0] === questionObject.response_id);
        const scoringRubricItemReference =
          scoringRubricItem.reference || scoringRubricItem.source.reference; //TODO - verify again
        const questionReference = questions.find(
          (q) =>
            q.children &&
            q.children.length &&
            !!q.children.find(
              (c) => c.learnosityItemReference === scoringRubricItemReference,
            ),
        ).learnosityItemReference;

        // If no score is entered, we need to get the maxScore from the rubric itself
        const maxScoreFallback = get(
          scoringRubricItem,
          'questions[0].options[0].max_score',
        );

        frqScores[scoringRubricItemReference] = {
          maxScore: rubricsMaxScore || maxScoreFallback,
          score: rubricsScore,
          questionReference,
          scored: !!questionResponse,
        };
      });

      const mcqScores = entries(learnosityState.scores).reduce((curr, next) => {
        const questionItem = values(items).find(
          (item) => item.response_ids.indexOf(next[0]) !== -1,
        );

        return {
          ...curr,
          [questionItem.reference]: {
            maxScore: next[1].max_score,
            score: next[1].score,
          },
        };
      }, {});

      dispatchScore({
        type: 'mcqQuestionScores',
        payload: { scores: mcqScores },
      });
      dispatchScore({
        type: 'frqQuestionScores',
        payload: { scores: frqScores },
      });
    }
  }, [isScoringRubricReady, isStudentSessionReady]);

  useEffect(() => {
    if (isStudentSessionReady) {
      const questionsResponses = questionsApp.getResponses();

      const questionReferences = entries(questionsResponses).reduce(
        (curr, next) => {
          const questionItem = values(items).find(
            (item) => item.response_ids.indexOf(next[0]) !== -1,
          );

          return [...curr, questionItem.reference];
        },
        [],
      );

      dispatchScore({
        type: 'answeredQuestions',
        payload: { questions: questionReferences },
      });
    }
  }, [isStudentSessionReady]);

  useEffect(() => {
    if (
      isSubmissionScored &&
      currentFRQScore !== null &&
      savedFRQScore === null
    ) {
      setSavedFRQScore(currentFRQScore);
    }
  }, [scoresState]);

  const toggleConfirmSubmitDialog = async () => {
    // if student and assignment was unsubmitted by teacher don't save scores
    refetchProgress && (await refetchProgress());
    setIsConfirmSubmitOpened(!isConfirmSubmitOpened);
  };

  const backLink = returnPath;

  const scoresByQuestion = {
    ...scoresState.mcqScores,
    ...values(scoresState.frqScores).reduce(
      (curr, next) => ({
        ...curr,
        [next.questionReference]: {
          maxScore: curr[next.questionReference]
            ? curr[next.questionReference].maxScore + next.maxScore
            : next.maxScore,
          score: curr[next.questionReference]
            ? curr[next.questionReference].score + next.score
            : next.score,
          scored: curr[next.questionReference]
            ? curr[next.questionReference].scored && next.scored
            : next.scored,
        },
      }),
      {},
    ),
  };

  async function submitScores() {
    try {
      setIsSubmitting(true);
      await onSubmit(
        scoringRubricItemsApp,
        studentResponseForReference,
        !showStudentScoringSwitch && backLink,
      );
      setIsConfirmSubmitOpened(false);
      setSavedScoringRubricResponses(currentScoringRubricResponses);
      setSavedFRQScore(currentFRQScore);
      setIsSubmitting(false);
    } catch (e) {
      setIsSubmitting(false);
      throw e;
    }
  }

  const hasChangedScoringRubricResponses = !isEqual(
    extractResponseValues(currentScoringRubricResponses),
    extractResponseValues(savedScoringRubricResponses),
  );

  const isSubmitButtonDisabled = disableAutoSaveOnReview
    ? showStudentScoringSwitch &&
      isSubmissionScored &&
      !hasChangedScoringRubricResponses
    : showStudentScoringSwitch && isSubmissionScored;

  const saveToBrowserKey = saveToBrowser
    ? `${assignment.id}-${scoringStudent.id}`
    : '';

  const hasStudentSubmittedAssignment = (studentId) =>
    submissions &&
    submissions[studentId] !== 'not_started' &&
    submissions[studentId] !== 'started';

  const studentDropdownItems =
    assignment.students &&
    sortBy(assignment.students, ['lastName', 'firstName']).map((student) => ({
      label: `${student.firstName} ${student.lastName}`,
      value: student.id,
      disabled: !(
        hasStudentSubmittedAssignment(student.id) || assignedAsPrintTest
      ),
    }));

  const switchStudent = (studentId) => {
    const { assignmentId, questionId } = params;
    navigateTo(
      `/score/${assignmentId}/students/${studentId}/questions/${questionId}`,
    );
  };

  let maxScore = null;
  if (maxFRQScore !== null) {
    // MCQs can be scored with either 0 or 1, so the max score that student can get for all MCQs equals the number of MCQs
    const maxMCQScore = questions.filter((question) => isMCQ(question)).length;
    maxScore = maxFRQScore + maxMCQScore;
  }
  const currentCumulativeScore =
    savedMCQScore !== null && currentFRQScore !== null
      ? savedMCQScore + currentFRQScore
      : null;
  const savedCumulativeScore =
    savedMCQScore !== null && savedFRQScore !== null
      ? savedMCQScore + savedFRQScore
      : null;

  let cumulativeConfirmDialogAria;
  let cumulativeConfirmDialogContent;
  if (isStudent) {
    // eslint-disable-next-line max-len
    cumulativeConfirmDialogAria = `An overall score of ${currentCumulativeScore}/${maxScore} points has been saved for this assignment. You will not be able to change your score once it is submitted.`;
    cumulativeConfirmDialogContent = () => (
      <Fragment>
        <p>
          An overall score of {currentCumulativeScore}/{maxScore} points has
          been saved for this assignment.
        </p>
        <p>You will not be able to change your score once it is submitted.</p>
      </Fragment>
    );
  } else {
    // eslint-disable-next-line max-len
    cumulativeConfirmDialogAria = `An overall score of ${currentCumulativeScore}/${maxScore} points has been saved for this assignment. If you’ve enabled student access to results after scoring, clicking submit will enable this student to view their results.`;
    cumulativeConfirmDialogContent = () => (
      <Fragment>
        <p>
          An overall score of {currentCumulativeScore}/{maxScore} points has
          been saved for this assignment.
        </p>
        <p>
          If you’ve enabled student access to results after scoring, clicking
          submit will enable this student to view their results.
        </p>
      </Fragment>
    );
  }

  const navigateTo = (targetURL, removeQuery = false) => {
    const shouldDisplayConfirmation = disableAutoSaveOnReview
      ? isSubmissionScored && hasChangedScoringRubricResponses
      : false;
    if (shouldDisplayConfirmation) {
      setNavigateToURL(targetURL);
    } else {
      pushPathname(targetURL, removeQuery, location.state);
    }
  };

  const showMobileVersion = MobileMedia.matches;

  const navigation = ({
    isListView,
    showIcons,
    showArrows,
    onQuestionClick = () => {},
  }) => (
    <ScoringNav
      showIcons={showIcons}
      showArrows={showArrows}
      showDoubleArrow={!showMobileVersion}
      isListView={isListView}
      charmUpdateFlag={charmUpdateFlag}
      preApCharmUpdateFlag={preApCharmUpdateFlag}
      isApSubject={isApSubject}
      questions={questions.map((question) => {
        // extend APC question with ids of features from learnosity
        // use to show connections between questions with same features (shared passages)
        const lrnItem = learnosityState.items[question.learnosityItemReference];
        return {
          ...question,
          featureIds: lrnItem && lrnItem.feature_ids,
        };
      })}
      answeredQuestions={answeredQuestions}
      scoresByQuestion={scoresByQuestion}
      currentQuestionIndex={visibleQuestionIndex}
      onNextClick={() => goToQuestion(true)}
      onQuestionClick={onQuestionClick}
      goToQuestion={goToQuestionIndex}
      onPreviousClick={() => goToQuestion(false)}
      onClose={showMobileVersion && onClose}
    />
  );

  const [navArrows, setNavArrows] = useState(null);

  const actions = (
    <div
      className="submit mobile:fixed z-10 inset-x-0 bottom-0
      mobile:px-4.4 mobile:py-3 bg-white mobile:border-t mobile:border-gray-300"
    >
      {navArrows}

      {isSubmitButtonDisabled ? (
        <div className="success-mark mx-6 bg-green-300 text-base text-center h-12.2 p-4 rounded-full">
          <Icon name="check" /> Submitted
        </div>
      ) : (
        <Button
          isPrimary
          className="mobile:w-full"
          aria-label="Open submit confirm dialog"
          type="submit"
          tabIndex="-1"
          onClick={toggleConfirmSubmitDialog}
          disabled={!isAllScored}
        >
          Submit
        </Button>
      )}
    </div>
  );

  return (
    <div className={cn('ScoringLayout', { isApReader })}>
      {showHeader && (
        <div
          className={cn(
            'header border-b border-solid border-gray-400',
            isApSubject ? 'ap-spacer' : 'preap-spacer',
          )}
        >
          <h1 className="title mobile:hidden text-base" data-test-header-title>
            {title}{' '}
            {!showStudentScoringSwitch &&
              !isApReader &&
              `for ${scoringStudent.firstName} ${scoringStudent.lastName}`}
          </h1>

          {showCumulativeScore && savedCumulativeScore !== null && maxScore && (
            <div
              className="cumulative-score text-base"
              aria-live="polite"
              aria-atomic="true"
            >
              {`${savedCumulativeScore}/${maxScore} points`}
            </div>
          )}

          {isApReader && (
            <span>
              Student Id: <strong>{params.studentId}</strong>
            </span>
          )}
          {Boolean(showStudentScoringSwitch) && (
            <div className="StudentFilter mr-3">
              <DropdownFilter
                className="student-name-dropdown"
                setActiveItem={(item) => switchStudent(item.value)}
                items={studentDropdownItems}
                keyValue={{ value: 'value', label: 'label' }}
                activeItem={studentDropdownItems.find(
                  (item) => item.value === scoringStudent.id,
                )}
                includeAll={false}
              />
            </div>
          )}

          {!isApReader && showActionButtons && actions}

          {!isApReader && (
            <div className="p-4 desktop:border-l border-gray-300 ml-6 pl-6 mobile:fixed z-10 right-6 top-4 flex">
              <ButtonWithIcon
                isLink
                isNaked
                aria-label="Go back to assignment progress"
                onClick={() => navigateTo(backLink, true)}
                className="desktop:pl-1"
                icon="xmark"
                iconOrientation="right"
              >
                <span className="text-14 desktop:hidden text-black">
                  Save & Exit
                </span>
              </ButtonWithIcon>
            </div>
          )}
        </div>
      )}

      {!isApReader && navigation({ showIcons: !showMobileVersion })}

      {showActionButtons && !isApReader && (
        <FeedbackButton
          tabIndex="-1"
          className="text-black"
          openByDefault={openFeedbackPanel}
          hasText={!showMobileVersion}
          onSaved={triggerAutoSave}
          headerText={
            isStudent
              ? 'Explain the scores you selected for this assignment'
              : undefined
          }
          text={assignmentFeedback}
          studentId={scoringStudent.id}
          assignmentId={id}
          studentName={`${scoringStudent.firstName} ${scoringStudent.lastName}`}
          size={30}
          saveToParent={(text) => setFeedback(text)}
          saveToBrowser={saveToBrowser}
          saveToBrowserKey={saveToBrowserKey}
          showFeedbackPanel
          isImpersonating={isImpersonating}
        />
      )}

      {questions.map((question, questionIndex) => (
        <ScoringItem
          renderNavArrows={(arrows) => setNavArrows(arrows)}
          isMobile={showMobileVersion}
          key={question.id}
          isFinalExam={isFinalExam}
          question={question}
          visibleQuestionIndex={visibleQuestionIndex}
          questionIndex={questionIndex}
          questionsWithApi={questionsWithApi}
          learnosityItems={items}
          questionsLength={questions.length}
          studentResponseForReference={studentResponseForReference}
          responseAnalysis={createResponseAnalysis(
            reportAnalysisData,
            question.learnosityItemReference,
          )}
          scoringStudent={scoringStudent}
          goToQuestion={goToQuestion}
          sectionToGo={sectionToGo}
          handleAutoSave={triggerAutoSave}
          responseTabLabel={isStudent ? 'Your Response' : 'Student Response'}
          isStudent={isStudent}
          subjectData={subjectData}
          featureFlags={featureFlags}
          isPrintTest={assignedAsPrintTest}
          setTabData={setTabData}
          tabData={tabData}
          responsesPage={responsesPage}
          isRubricsDisabled={isApReader}
          params={params}
        />
      ))}

      {studentSessionReviewSignedRequest && (
        <LearnosityProvider
          initOptions={studentSessionReviewSignedRequest}
          apiType="items"
          readyListener={(payload) => {
            dispatch({ type: 'onStudentReviewReady', payload });
          }}
        />
      )}

      {!isApReader &&
        scoringRubricSignedRequest &&
        isStudentSessionReady &&
        (scoringRubricResponseAnalysisSignedRequest
          ? learnosityState.scoringReportAnalysisData
          : true) && (
          <LearnosityProvider
            initOptions={scoringRubricSignedRequest}
            apiType="items"
            readyListener={(payload) =>
              dispatch({ type: 'onScoringRubricReady', payload })
            }
          />
        )}

      {responseAnalysisSignedRequest && (
        <LearnosityProvider
          containerElement="learnosity_report_response_analysis"
          initOptions={responseAnalysisSignedRequest}
          apiType="reports"
          dataListener={(payload) =>
            dispatch({ type: 'onLearnosityDataResponseAnalysisReady', payload })
          }
        />
      )}

      {showCumulativeScore && isConfirmSubmitOpened && (
        <ConfirmDialog
          title="Submit Score for this Assignment?"
          contentLabel={`Confirm Dialog: ${cumulativeConfirmDialogAria}`}
          onClose={toggleConfirmSubmitDialog}
          onSuccess={submitScores}
          aria={{
            live: 'polite',
          }}
          content={{
            btnSecondary: 'Cancel',
            btnPrimary: isSubmitting ? 'Submitting...' : 'Submit',
          }}
        >
          {cumulativeConfirmDialogContent
            ? cumulativeConfirmDialogContent()
            : null}
        </ConfirmDialog>
      )}
      {!showCumulativeScore && isConfirmSubmitOpened && (
        <ConfirmDialog
          size="tiny"
          title="Submit"
          contentLabel="Confirm Dialog: Are you sure you want to submit ?"
          onClose={toggleConfirmSubmitDialog}
          onSuccess={submitScores}
          aria={{
            live: 'polite',
          }}
          content={{
            btnSecondary: 'No',
            btnPrimary: isSubmitting ? 'Submitting...' : 'Yes',
          }}
        >
          <p>Are you sure you want to submit ?</p>
        </ConfirmDialog>
      )}
      {navigateToURL && (
        <ConfirmDialog
          title="Updating Score?"
          contentLabel="Updating Score: You’ve selected a new score for this assignment. Would you like to update this student’s score?"
          onClose={() => {
            setNavigateToURL(null);
          }}
          onSecondaryBtnClick={() => {
            pushPathname(navigateToURL, false, location.state);
          }}
          onSuccess={async () => {
            await submitScores();
            pushPathname(navigateToURL, false, location.state);
          }}
          aria={{
            live: 'polite',
          }}
          content={{
            btnSecondary: 'Don’t Update',
            btnPrimary: isSubmitting ? 'Updating...' : 'Update Score',
          }}
        >
          <p>
            You’ve selected a new score for this assignment. Would you like to
            update this student’s score?
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

ScoringLayout.propTypes = {
  studentSessionReviewSignedRequest: PropTypes.object,
  scoringRubricSignedRequest: PropTypes.object,
  params: PropTypes.object,
  questions: PropTypes.array,

  pushPathname: PropTypes.func,
  onSubmit: PropTypes.func,
  onSave: PropTypes.func,

  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),

  scoringStudent: PropTypes.object,
  showHeader: PropTypes.bool,
  isStudent: PropTypes.bool,
  subjectData: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  featureFlags: PropTypes.object,
  saveToBrowser: PropTypes.bool,

  isImpersonating: PropTypes.bool,
};

ScoringLayout.defaultProps = {
  studentSessionReviewSignedRequest: {},
  scoringRubricSignedRequest: {},
  params: {},
  questions: [],

  onSubmit: () => {},
  onSave: () => {},
  pushPathname: () => {},

  location: {
    pathname: '',
  },

  scoringStudent: {},
  assignmentTitle: '',
  showHeader: true,
  canSave: true,
  showActionButtons: true,
  isStudent: false,
  subjectData: {},
  featureFlags: {},
  saveToBrowser: false,

  isImpersonating: false,
};

export default ScoringLayout;
